import {
  Component,
  ChangeDetectionStrategy,
  computed,
  DestroyRef,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Loading } from '../../../../../utils/loading/loading';
import { EmptyData } from '../../../../../utils/empty-data/empty-data';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ScopeResolver, Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import { OrderListItem } from '../../../../../pages/admin-dashboard/orders-management/orders.model';
import { toOrderTableRows } from './order-table.mapper';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { SCOPES } from '../../../../../services/scope/scope-constants';
import { OrderActionPopup } from '../../../../../shared/ui/order-action-popup/order-action-popup';
import { finalize } from 'rxjs';
import { ActionsService } from '../../../../../services/actions/actions.service';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import { AuthService } from '../../../../../services/auth-service';
import { OrderService } from '../../../../../services/orders/order.service';

export type LoadState = 'loading' | 'success' | 'error';

export type OrderListAction =
  'cancel' | 'reject-review' | 'confirm-review' | 'ship' | 'repackage' | 'receive-returned' | 'collect-cash';

const ACTIONS: Record<string, ActionInterface> = {
  print: { id: 1, titleEn: 'Print', titleAr: 'طباعة' },
  cancel: { id: 2, titleEn: 'Cancel', titleAr: 'إلغاء' },
  editOrder: { id: 3, titleEn: 'Edit Order', titleAr: 'تعديل الطلب' },
  package: { id: 4, titleEn: 'Package', titleAr: 'تعبئة' },
  confirmPackage: { id: 5, titleEn: 'Confirm Package', titleAr: 'تأكيد التعبئة' },
  approve: { id: 6, titleEn: 'Confirm Review', titleAr: 'تأكيد المراجعة' },
  reject: { id: 7, titleEn: 'Reject Review', titleAr: 'رفض المراجعة' },
  ship: { id: 8, titleEn: 'Ship', titleAr: 'شحن' },
  repackage: { id: 9, titleEn: 'Repackage', titleAr: 'إعادة التعبئة' },
  deliver: { id: 10, titleEn: 'Deliver', titleAr: 'تسليم' },
  undeliver: { id: 11, titleEn: 'Undeliver', titleAr: 'عدم تسليم' },
  partialDeliver: { id: 12, titleEn: 'Partial Deliver', titleAr: 'تسليم جزئي' },
  collectCash: { id: 13, titleEn: 'Collect Cash', titleAr: 'تحصيل نقدي' },
  receiveReturned: { id: 14, titleEn: 'Receive Returned Product', titleAr: 'استلام منتج مرتجع' },
  viewDetails: { id: 260115, titleEn: 'View Details', titleAr: 'عرض التفاصيل' },
};

const ACTION_SCOPES: Record<number, number | null> = {
  [ACTIONS['print'].id]: SCOPES.ORDERS.PRINT,
  [ACTIONS['cancel'].id]: SCOPES.ORDERS.CANCEL,
  [ACTIONS['package'].id]: SCOPES.ORDERS.PACKAGE,
  [ACTIONS['confirmPackage'].id]: SCOPES.ORDERS.SUBMIT_PACKAGE,
  [ACTIONS['approve'].id]: SCOPES.ORDERS.CONFIRM_REVIEW,
  [ACTIONS['reject'].id]: SCOPES.ORDERS.CONFIRM_REVIEW,
  [ACTIONS['ship'].id]: SCOPES.ORDERS.SHIP,
  [ACTIONS['repackage'].id]: SCOPES.ORDERS.REPACKAGE,
  [ACTIONS['deliver'].id]: SCOPES.ORDERS.DELIVER,
  [ACTIONS['undeliver'].id]: SCOPES.ORDERS.UNDELIVER,
  [ACTIONS['collectCash'].id]: SCOPES.ORDERS.COLLECT_CASH,
  [ACTIONS['viewDetails'].id]: SCOPES.ORDERS.READ,
};

type StatusId = number;

const STATUS_ACTIONS: Record<StatusId, ActionInterface[]> = {
  260101: [ACTIONS['viewDetails'], ACTIONS['print'], ACTIONS['cancel'], ACTIONS['editOrder']],
  260102: [ACTIONS['viewDetails'], ACTIONS['package'], ACTIONS['cancel']],
  260103: [ACTIONS['viewDetails'], ACTIONS['print'], ACTIONS['cancel']],
  260104: [ACTIONS['viewDetails']],
  260105: [ACTIONS['viewDetails'], ACTIONS['approve'], ACTIONS['reject'], ACTIONS['cancel']],
  260106: [ACTIONS['viewDetails'], ACTIONS['confirmPackage'], ACTIONS['editOrder'], ACTIONS['cancel']],
  260107: [ACTIONS['viewDetails'], ACTIONS['repackage'], ACTIONS['cancel']],
  260108: [ACTIONS['viewDetails'], ACTIONS['ship'], ACTIONS['cancel']],
  260109: [ACTIONS['viewDetails'], ACTIONS['deliver'], ACTIONS['undeliver'], ACTIONS['partialDeliver']],
  260110: [ACTIONS['viewDetails'], ACTIONS['collectCash']],
  260111: [ACTIONS['viewDetails'], ACTIONS['receiveReturned'], ACTIONS['collectCash']],
  260112: [ACTIONS['viewDetails'], ACTIONS['receiveReturned']],
  260113: [ACTIONS['viewDetails']],
  260114: [ACTIONS['viewDetails']],
  260115: [ACTIONS['viewDetails']],
};

@Component({
  selector: 'app-order-table',
  standalone: true,
  imports: [DatePipe, Loading, EmptyData, ErrorData, Tables, Pagination, OrderActionPopup, SuccessfulNotification],
  templateUrl: './order-table.html',
  styleUrl: './order-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderTable {
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private actionsService = inject(ActionsService);
  private orderService = inject(OrderService);

  orders = input<OrderListItem[] | null>(null);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);

  pageChange = output<number>();
  getOrders = output<void>();
  pageSizeChange = output<number>();
  rowClick = output<number>();
  orderPrinted = output<{ orderId: string; statusId: number }>();
  openUndeliver = output<number>();
  actionClick = output<{ action: OrderListAction; order: OrderListItem }>();

  rows = computed(() => toOrderTableRows(this.orders() ?? [], this.isArabic()));

  activeDropdownId = signal<number | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  isDeliverPopup = signal(false);
  delivering = signal(false);
  errDeliver = signal('');
  selectedOrderForDeliver = signal<OrderListItem | null>(null);
  deliverAmountCollected = signal<number | null>(null);
  successDeliverNotification = signal(false);

  expectedAmount = computed(() => {
    const order = this.selectedOrderForDeliver() as any;
    if (!order) return 0;
    const value = Number(
      order.totalBusinessPrice ?? order.totalPharmacyPrice ?? order.totalStorePrice ?? order.totalPrice ?? 0,
    );
    return Math.round(value * 100) / 100;
  });

  tableHeaderData = signal([
    { key: 'orderId', label: $localize`:@@adminOrdersTableHeaderNum:Order ID` },
    { key: 'pharmacyOwnerRegion', label: $localize`:@@adminOrdersTableHeaderPharmacy:Pharmacy Owner + Region` },
    { key: 'status', label: $localize`:@@adminOrdersTableHeaderStatus:Status`, type: 'status' },
    { key: 'totalItems', label: $localize`:@@adminOrdersTableHeaderTotalQty:Total Items` },
    {
      key: 'totalPharmacyPrice',
      label: $localize`:@@adminOrdersTableHeaderTotalPrice:Total Pharmacy Price`,
      type: 'currency',
    },
    { key: 'createdOnBy', label: $localize`:@@adminOrdersTableHeaderCreatedOn:Created On / By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@adminOrdersTableHeaderLastUpdated:Last Modified On / By` },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  printPopup = signal(false);
  printing = signal(false);
  errPrintAction = signal('');
  orderId = signal<string | null>(null);
  successNotification = signal(false);

  isArabic() {
    return this.locale.startsWith('ar');
  }

  getActions(order: OrderListItem): ActionInterface[] {
    const statusId = order.status?.id as StatusId;
    return statusId ? (STATUS_ACTIONS[statusId] ?? []) : [];
  }

  toggleDropdown(id?: any) {
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const order = this.orders()?.find((o) => o.id === id);
      if (order) this.currentActions.set(this.getActions(order));
    }
  }

  onActivePrintPopup(orderId: string) {
    this.orderId.set(orderId);
    this.printPopup.set(true);
  }

  onCancelPrintPopup() {
    this.printPopup.set(false);
  }

  onPrint(comment: string) {
    const id = this.orderId();
    if (!id) return;
    this.printing.set(true);
    const subscription = this.actionsService
      .printOrder(id, comment)
      .pipe(
        finalize(() => {
          this.printing.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.successNotification.set(true);
          setTimeout(() => {
            this.successNotification.set(false);
          }, 3000);
          this.orderPrinted.emit({ orderId: id, statusId: 260102 });

          this.onCancelPrintPopup();
          const foundOrder = this.orders()?.find((o) => +o.id === +id);
          if (foundOrder) {
            this.getActions(foundOrder);
          }
          this.activeDropdownId.set(null);
          this.orderId.set(null);
        },
        error: (err: any) => {
          this.errPrintAction.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ' : err.error.messageEn || 'some error occured!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private resolveOrder(entity: any): OrderListItem {
    return this.orders()?.find((o) => +o.id === +(entity?.id ?? entity?.orderId)) ?? entity;
  }

  private emitAction(action: OrderListAction, entity: any) {
    this.activeDropdownId.set(null);
    this.actionClick.emit({ action, order: this.resolveOrder(entity) });
  }

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event, actionId, entity } = e;
    event?.stopPropagation();
    switch (actionId) {
      case 1:
        this.onActivePrintPopup(entity.orderId ?? entity.id);
        break;

      case 2:
        this.emitAction('cancel', entity);
        break;

      case 3:
      case 4:
      case 5:
      case 12:
        this.rowClick.emit(entity.id);
        break;

      case 6:
        this.emitAction('confirm-review', entity);
        break;

      case 7:
        this.emitAction('reject-review', entity);
        break;

      case 8:
        this.emitAction('ship', entity);
        break;

      case 9:
        this.emitAction('repackage', entity);
        break;

      case 10:
        this.openDeliverPopup(entity);
        break;

      case 11:
        this.openUndeliver.emit(entity.id);
        break;

      case 13:
        this.emitAction('collect-cash', entity);
        break;

      case 14:
        this.emitAction('receive-returned', entity);
        break;

      case 260115:
        this.rowClick.emit(entity.id);
        break;
    }
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }

  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }

  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }

  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }

  onPageSizeChange() {}

  orderScopeResolver: ScopeResolver = (action) => ACTION_SCOPES[action.id] ?? null;

  openDeliverPopup(order: OrderListItem | any) {
    const fullOrder = this.orders()?.find((o) => +o.id === +(order?.id ?? order?.orderId)) ?? order;

    this.selectedOrderForDeliver.set(fullOrder);
    this.deliverAmountCollected.set(null);
    this.errDeliver.set('');
    this.isDeliverPopup.set(true);
    this.activeDropdownId.set(null);
  }

  onCloseDeliver() {
    this.isDeliverPopup.set(false);
    this.selectedOrderForDeliver.set(null);
    this.deliverAmountCollected.set(null);
    this.errDeliver.set('');
    this.delivering.set(false);
  }

  onDeliverOrder(comment: string, amountCollected?: number | null) {
    const order = this.selectedOrderForDeliver();
    if (!order?.id) return;

    this.delivering.set(true);
    this.errDeliver.set('');

    const payload: { comment?: string; amountCollected?: number } = {
      comment: comment || '',
    };

    if (amountCollected !== null && amountCollected !== undefined && !Number.isNaN(Number(amountCollected))) {
      payload.amountCollected = Number(amountCollected);
    }

    const subscription = this.orderService
      .deliverOrder(String(order.id), payload as any)
      .pipe(finalize(() => this.delivering.set(false)))
      .subscribe({
        next: () => {
          this.onCloseDeliver();
          this.successDeliverNotification.set(true);
          setTimeout(() => this.successDeliverNotification.set(false), 3000);
          this.getOrders.emit();
        },
        error: (err: any) => {
          this.errDeliver.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث بعض الخطأ' : err?.error?.messageEn || 'some error occured!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
